import {
  BUILDING_BACKDROP_RGB_TOLERANCE,
  removeBackdropEdgeFlood,
} from "@cold-case/shared/building-texture-alpha";

/** Phaser Textures do not support GameObject-style getData/setData — track cleaned keys here. */
const cleanedBuildingTextureKeys = new Set<string>();

/** Re-bake a Phaser texture after stripping flat backdrop mats from building PNGs. */
export function cleanBuildingTextureInPlace(
  scene: Phaser.Scene,
  textureKey: string
): boolean {
  if (!textureKey.startsWith("bld_art_")) return false;
  if (cleanedBuildingTextureKeys.has(textureKey)) return false;
  const tex = scene.textures.get(textureKey);
  if (!tex) return false;

  const src = tex.getSourceImage() as HTMLImageElement | HTMLCanvasElement;
  const w = "naturalWidth" in src ? src.naturalWidth : src.width;
  const h = "naturalHeight" in src ? src.naturalHeight : src.height;
  if (w < 2 || h < 2) return false;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;

  ctx.drawImage(src, 0, 0);
  const imageData = ctx.getImageData(0, 0, w, h);
  removeBackdropEdgeFlood(imageData.data, w, h, {
    rgbTolerance: BUILDING_BACKDROP_RGB_TOLERANCE,
    skipTopEdge: true,
  });
  ctx.putImageData(imageData, 0, 0);

  if (scene.textures.exists(textureKey)) {
    scene.textures.remove(textureKey);
  }
  scene.textures.addCanvas(textureKey, canvas);
  const cleaned = scene.textures.get(textureKey);
  cleaned?.setFilter(Phaser.Textures.FilterMode.LINEAR);
  cleaned?.refresh();
  cleanedBuildingTextureKeys.add(textureKey);
  return true;
}

/** Clear cleanup cache (e.g. when textures are reloaded). */
export function resetBuildingTextureCleanupCache(): void {
  cleanedBuildingTextureKeys.clear();
}

export function cleanAllBuildingTextures(scene: Phaser.Scene): number {
  let n = 0;
  for (const key of scene.textures.getTextureKeys()) {
    if (cleanBuildingTextureInPlace(scene, key)) n++;
  }
  return n;
}
